import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { adminLoginThunk } from "../../redux/slices/authSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiEye, FiEyeOff, FiMenu, FiX } from "react-icons/fi";
import { Link } from "react-scroll";

const AdminLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, role } = useSelector((state) => state.auth);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (isAuthenticated && role === "admin") {
      navigate("/admin-dashboard");
    }
  }, [isAuthenticated, role, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(adminLoginThunk(formData));

    if (adminLoginThunk.fulfilled.match(result)) {
      toast.success("Admin login successful!");
      setTimeout(() => {
        navigate("/admin-dashboard");
      }, 1500);
    } else {
      toast.error("Invalid admin credentials. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <nav className={`border border-gray-200 bg-white z-50 rounded-[50px] shadow-lg py-3 px-4 md:px-8 ${
        windowWidth < 768 ? 
        'fixed top-4 left-1/2 transform -translate-x-1/2 w-[90%]' : 
        'absolute w-[90%] mt-4 ml-16'
      }`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div onClick={() => navigate("/")} className="cursor-pointer">
            <img
              src="img/newlogo.png"
              alt="Logo"
              className="h-10 md:h-14 scale-125 pl-3"
            />
          </div>
          
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <FiX className="h-6 w-6" />
              ) : (
                <FiMenu className="h-6 w-6" />
              )}
            </button>
          </div>

          <ul className="hidden md:flex space-x-4 lg:space-x-8 font-semibold text-black text-[15px] lg:text-[17px]">
            <li onClick={() => navigate("/")} className="cursor-pointer hover:text-blue-500">
              Home
            </li>
            <li onClick={() => navigate("/disease-details")} className="cursor-pointer hover:text-blue-500">
              Disease Details
            </li>
            <li onClick={() => navigate("/team")} className="cursor-pointer hover:text-blue-500">
              About our Team
            </li>
            <li
                onClick={() => {
                  navigate("/admin-entry");
                  setMobileMenuOpen(false);
                }}
                className="cursor-pointer hover:text-blue-500 text-center "
              >
                Restricted Route
              </li>
          </ul>

          <div className="hidden md:block">
            <Link
              to="get-started-section"
              smooth={true}
              duration={500}
              className="bg-blue-500 text-white px-4 py-1 md:px-6 md:py-2 rounded-[50px] cursor-pointer hover:bg-blue-600 transition text-sm md:text-base"
            >
              Get Started
            </Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4">
            <ul className="flex flex-col space-y-4">
              <li
                onClick={() => {
                  navigate("/");
                  setMobileMenuOpen(false);
                }}
                className="cursor-pointer hover:text-blue-500 text-center py-2"
              >
                Home
              </li>
              <li
                onClick={() => {
                  navigate("/disease-details");
                  setMobileMenuOpen(false);
                }}
                className="cursor-pointer hover:text-blue-500 text-center py-2"
              >
                Disease Details
              </li>
              <li
                onClick={() => {
                  navigate("/team");
                  setMobileMenuOpen(false);
                }}
                className="cursor-pointer hover:text-blue-500 text-center py-2"
              >
                About our Team
              </li>
              <li
                onClick={() => {
                  navigate("/admin-entry");
                  setMobileMenuOpen(false);
                }}
                className="cursor-pointer hover:text-blue-500 text-center py-2"
              >
                Restricted Route
              </li>
              <li className="text-center pt-2">
                <Link
                  to="get-started-section"
                  smooth={true}
                  duration={500}
                  className="inline-block bg-blue-500 text-white px-6 py-2 rounded-[50px] cursor-pointer hover:bg-blue-600 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>

      <div className="flex-1 font-semibold flex items-center justify-center p-4 mt-16 md:mt-20">
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg w-full max-w-md">
          <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-4 md:mb-6">
            Admin Login
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div>
              <label htmlFor="email" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                placeholder="Enter admin email"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 text-sm md:text-base"
              >
                Login
              </button>
            </div>
            <br></br>
            {/* <div className="text-center text-xs md:text-sm text-gray-500">
              <p>Use the predefined admin credentials:</p>
              <p>Email: rohitadmin45@ymail.com</p>
              <p>Password: Rohitbhai@45</p>
            </div> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;


// //---------------- updated code for red alerts-------------
// import { useState, useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { adminLoginThunk } from "../../redux/slices/authSlice";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { FiEye, FiEyeOff, FiMenu, FiX } from "react-icons/fi";
// import { Link } from "react-scroll";

// const AdminLogin = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { isAuthenticated, role } = useSelector((state) => state.auth);

//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);
//   const [showPassword, setShowPassword] = useState(false);

//   const [formData, setFormData] = useState({
//     email: "",
//     password: "",
//   });

//   const [validationErrors, setValidationErrors] = useState({});

//   useEffect(() => {
//     const handleResize = () => setWindowWidth(window.innerWidth);
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     if (isAuthenticated && role === "admin") {
//       navigate("/admin-dashboard");
//     }
//   }, [isAuthenticated, role, navigate]);

//   const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
//   const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({ ...formData, [name]: value });
//     setValidationErrors({ ...validationErrors, [name]: "" });
//   };

//   const validateForm = () => {
//     const errors = {};
//     if (!formData.email.trim()) {
//       errors.email = "Email is required";
//     } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
//       errors.email = "Enter a valid email address";
//     }

//     if (!formData.password.trim()) {
//       errors.password = "Password is required";
//     } else if (formData.password.length < 8) {
//       errors.password = "Min 8 chars with letter, number & symbol!";
//     }

//     setValidationErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) return;

//     const result = await dispatch(adminLoginThunk(formData));

//     if (adminLoginThunk.fulfilled.match(result)) {
//       toast.success("Admin login successful!");
//       setTimeout(() => {
//         navigate("/admin-dashboard");
//       }, 1500);
//     } else {
//       toast.error("Invalid admin credentials. Please try again.");
//     }
//   };

//   return (
//     <div className="min-h-screen bg-gray-100 flex flex-col font-semibold">
//       <ToastContainer position="top-right" autoClose={3000} />

//       {/* NAVBAR */}
//       <nav
//         className={`border border-gray-200 bg-white z-50 rounded-[50px] shadow-lg py-3 px-4 md:px-8 ${
//           windowWidth < 768
//             ? "fixed top-4 left-1/2 transform -translate-x-1/2 w-[90%]"
//             : "absolute w-[90%] mt-4 ml-16"
//         }`}
//       >
//         <div className="max-w-7xl mx-auto flex justify-between items-center">
//           <div onClick={() => navigate("/")} className="cursor-pointer">
//             <img
//               src="img/newlogo.png"
//               alt="Logo"
//               className="h-10 md:h-14 scale-125 pl-3"
//             />
//           </div>

//           <div className="md:hidden">
//             <button onClick={toggleMobileMenu} className="text-gray-700">
//               {mobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
//             </button>
//           </div>

//           <ul className="hidden md:flex space-x-4 lg:space-x-8 font-semibold text-black text-[15px] lg:text-[17px]">
//             <li onClick={() => navigate("/")} className="cursor-pointer hover:text-blue-500">Home</li>
//             <li onClick={() => navigate("/disease-details")} className="cursor-pointer hover:text-blue-500">Disease Details</li>
//             <li onClick={() => navigate("/team")} className="cursor-pointer hover:text-blue-500">About our Team</li>
//             <li onClick={() => navigate("/admin-entry")} className="cursor-pointer hover:text-blue-500">Restricted Route</li>
//           </ul>

//           <div className="hidden md:block">
//             <Link
//               to="get-started-section"
//               smooth={true}
//               duration={500}
//               className="bg-blue-500 text-white px-4 py-1 md:px-6 md:py-2 rounded-[50px] cursor-pointer hover:bg-blue-600 transition text-sm md:text-base"
//             >
//               Get Started
//             </Link>
//           </div>
//         </div>

//         {mobileMenuOpen && (
//           <div className="md:hidden mt-4 pb-4">
//             <ul className="flex flex-col space-y-4">
//               {["/", "/disease-details", "/team", "/admin-entry"].map((route, i) => (
//                 <li
//                   key={i}
//                   onClick={() => {
//                     navigate(route);
//                     setMobileMenuOpen(false);
//                   }}
//                   className="cursor-pointer hover:text-blue-500 text-center py-2"
//                 >
//                   {["Home", "Disease Details", "About our Team", "Restricted Route"][i]}
//                 </li>
//               ))}
//               <li className="text-center pt-2">
//                 <Link
//                   to="get-started-section"
//                   smooth={true}
//                   duration={500}
//                   className="inline-block bg-blue-500 text-white px-6 py-2 rounded-[50px] hover:bg-blue-600 transition"
//                   onClick={() => setMobileMenuOpen(false)}
//                 >
//                   Get Started
//                 </Link>
//               </li>
//             </ul>
//           </div>
//         )}
//       </nav>

//       {/* LOGIN FORM */}
//       <div className="flex-1 flex items-center justify-center p-4 mt-16 md:mt-20">
//         <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg w-full max-w-md">
//           <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-4 md:mb-6">
//             Admin Login
//           </h2>

//           <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
//             {/* Email Field */}
//             <div>
//               <label htmlFor="email" className="block text-gray-700 mb-1 md:mb-2 text-sm md:text-base">
//                 Email
//               </label>
//               <input
//                 type="email"
//                 id="email"
//                 name="email"
//                 value={formData.email}
//                 onChange={handleChange}
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
//                 placeholder="Enter admin email"
//               />
//               {validationErrors.email && (
//                 <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
//               )}
//             </div>

//             {/* Password Field */}
//             <div>
//               <label htmlFor="password" className="block text-gray-700 mb-1 md:mb-2 text-sm md:text-base">
//                 Password
//               </label>
//               <div className="relative">
//                 <input
//                   type={showPassword ? "text" : "password"}
//                   id="password"
//                   name="password"
//                   value={formData.password}
//                   onChange={handleChange}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
//                   placeholder="Enter admin password"
//                 />
//                 <button
//                   type="button"
//                   onClick={togglePasswordVisibility}
//                   className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
//                 >
//                   {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
//                 </button>
//               </div>
//               {validationErrors.password && (
//                 <p className="text-red-500 text-sm mt-1">{validationErrors.password}</p>
//               )}
//             </div>

//             {/* Submit Button */}
//             <div>
//               <button
//                 type="submit"
//                 className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 text-sm md:text-base"
//               >
//                 Login
//               </button>
//             </div>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AdminLogin;
